import {Alert, Checkbox, Text} from '@mantine/core'
import type {UseFormReturnType} from '@mantine/form'
import {IconInfoCircle} from '@tabler/icons-react'
import {useEffect} from 'react'

import {formatPrice} from '../lib/format-price'
import type {CreateProjectFormValues} from './create-project-form-values'
import type {ProjectBillingMethod} from './schemas'

export function isBelowMinimumHourlyRate(
    activeMinimumHourlyRate: number | null,
    transportationHourlyRate: number | null,
    billingMethod: ProjectBillingMethod,
): boolean {
    if (billingMethod !== 'hourly_transportation') {
        return false
    }
    if (activeMinimumHourlyRate == null || transportationHourlyRate == null) {
        return false
    }
    return transportationHourlyRate < activeMinimumHourlyRate
}

type BelowMinimumRateAcknowledgementProps = {
    form: UseFormReturnType<CreateProjectFormValues>
    activeMinimumHourlyRate: number | null
    policyError: Error | null
    policyIsLoading?: boolean
}

export function BelowMinimumRateAcknowledgement({
    form,
    activeMinimumHourlyRate,
    policyError,
    policyIsLoading,
}: BelowMinimumRateAcknowledgementProps) {
    const {billingMethod, billingTerms} = form.values
    const isBelow = isBelowMinimumHourlyRate(
        activeMinimumHourlyRate,
        billingTerms.transportationHourlyRate,
        billingMethod,
    )

    useEffect(() => {
        if (!isBelow && billingTerms.managerApprovedBelowMinimumRate) {
            form.setFieldValue('billingTerms.managerApprovedBelowMinimumRate', false)
        }
    }, [isBelow, billingTerms.managerApprovedBelowMinimumRate, form])

    if (policyError) {
        return (
            <Alert color="red" icon={<IconInfoCircle size={16} />} title="Policy unavailable">
                <Text size="sm">
                    Could not load the active transportation rate policy. You can continue without
                    manager approval, but the rate minimum could not be checked.
                </Text>
            </Alert>
        )
    }

    if (policyIsLoading || activeMinimumHourlyRate == null || !isBelow) {
        return null
    }

    return (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
                The entered rate {formatPrice(billingTerms.transportationHourlyRate ?? 0)}/hr is
                below the company minimum of {formatPrice(activeMinimumHourlyRate)}/hr. Manager
                approval is required.
            </Text>
            <Checkbox
                mt="sm"
                label="A manager has approved this below-minimum rate"
                checked={billingTerms.managerApprovedBelowMinimumRate}
                onChange={(event) =>
                    form.setFieldValue(
                        'billingTerms.managerApprovedBelowMinimumRate',
                        event.currentTarget.checked,
                    )
                }
                error={form.errors['billingTerms.managerApprovedBelowMinimumRate']}
            />
        </Alert>
    )
}
